import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import { toast } from "react-toastify";
import axiosInstance from "utils/services/api";
import * as Yup from "yup";

const ChangePasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field) =>
    setVisible((prev) => ({ ...prev, [field]: !prev[field] }));

  const initialValues = {
    currentPassword: "",
    password: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    currentPassword: Yup.string()
      .trim()
      .required("Current Password is required"),
    password: Yup.string()
      .trim()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,14}$/,
        "Password should be 8-14 characters with at least one lowercase, one uppercase, one number, and one special character from @$!%*?&#."
      ),
    confirmPassword: Yup.string()
      .trim()
      .required("Confirm Password is required")
      .oneOf(
        [Yup.ref("password"), null],
        "Password and Confirm Password must match."
      ),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/users/update-password", {
        password: values.currentPassword,
        newPassword: values.password,
      });

      toast.success(response?.data?.message);
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const renderField = (id, name, label, visibleKey, placeholder) => (
    <div className="mb-3">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label} <span className="text-red-700">*</span>
      </label>
      <div className="relative">
        <Field
          type={visible[visibleKey] ? "text" : "password"}
          id={id}
          name={name}
          placeholder={placeholder}
          autoComplete={id}
          className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-700 dark:border-white/10 dark:text-white"
        />
        <span
          className="absolute right-3 top-4 cursor-pointer"
          onClick={() => toggleVisibility(visibleKey)}
        >
          {visible[visibleKey] ? <RiEyeLine /> : <RiEyeOffLine />}
        </span>
      </div>
      <ErrorMessage
        name={name}
        component="div"
        className="mt-1 text-sm text-red-500"
      />
    </div>
  );

  return (
    <div className="mb-16 mt-16 flex h-full w-full items-center justify-center px-2">
      <div className="mt-[10vh] w-full max-w-[500px] flex-col items-center md:pl-4">
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-900">
          <h4 className="mb-2.5 text-center text-4xl font-bold text-navy-700">
            Change Password
          </h4>
          <p className="mb-9 text-center text-base text-gray-600">
            Please enter your details to proceed securely.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              {renderField(
                "currentPassword",
                "currentPassword",
                "Current Password",
                "current",
                "Enter current password"
              )}
              {renderField(
                "password",
                "password",
                "New Password",
                "new",
                "Enter new password"
              )}
              {renderField(
                "confirmPassword",
                "confirmPassword",
                "Confirm Password",
                "confirm",
                "Enter confirm password"
              )}

              <button
                type="submit"
                className="mt-2 w-full rounded-xl bg-blueSecondary py-3 text-base font-medium text-white transition duration-200 active:bg-[#14AADD] dark:bg-brand-400 dark:hover:bg-brand-300"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </Form>
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordForm;